import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useServerFn } from "@tanstack/react-start";
import { BookingDocument } from "@/components/site/BookingDocument";
import { acceptBooking, rejectBooking, getBookingByToken } from "@/lib/bookings.functions";
import { depositAmount, formatGBP, paymentReference } from "@/lib/booking";
import { SITE } from "@/config/site";

export const Route = createFileRoute("/booking/$token")({
  head: () => ({
    meta: [
      { title: "Your Client Quotation | IZHUB Cleaning Services" },
      { name: "description", content: "Review your selected cleaning services, quotation and 25% deposit, then respond to IZHUB." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Your IZHUB Client Quotation" },
      { property: "og:description", content: "Review your selected services and price, pay your 25% deposit or reject the quotation." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: BookingPage,
});

function BookingPage() {
  const { token } = Route.useParams();
  const qc = useQueryClient();
  const [response, setResponse] = useState<"paid" | "rejected" | "">("");
  const acceptFn = useServerFn(acceptBooking);
  const rejectFn = useServerFn(rejectBooking);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const q = useQuery({
    queryKey: ["booking", token],
    queryFn: () => getBookingByToken({ data: { token } }),
    retry: false,
  });

  if (q.isLoading) return <Shell><p>Loading your booking…</p></Shell>;
  const b = q.data;
  if (!b) return <Shell><h1 className="font-display text-2xl">Booking not found</h1><p className="mt-2 text-muted-foreground">This link may be out of date. Please contact us on {SITE.phoneDisplay}.</p></Shell>;

  const price = b.final_price ?? 0;
  const deposit = depositAmount(price, b.deposit_percent);
  const ref = paymentReference(b.client_name, b.job_date);

  async function accept() {
    setBusy(true); setErr("");
    try {
      const result = response === "rejected" ? await rejectFn({ data: { token } }) : await acceptFn({ data: { token } });
      if (!result.ok) setErr("This booking has already changed. Please review its latest status.");
      await qc.invalidateQueries({ queryKey: ["booking", token] });
    } catch (e) { setErr(e instanceof Error ? e.message : "Something went wrong."); }
    setBusy(false);
  }

  return (
    <Shell>
      <BookingDocument booking={b} reference={ref} />

      {b.status === "confirmed" && (
        <Notice><CheckCircle2 className="h-5 w-5" /> You're booked! We've received your deposit — see you on {b.job_date || "the agreed date"}.</Notice>
      )}
      {b.status === "accepted" && (
        <Notice>Thanks — you've told us your deposit is paid. We'll confirm your booking once the payment arrives in our account.</Notice>
      )}
      {b.status === "rejected" && <Notice>You have rejected this job. No booking has been confirmed. Please contact us if you'd like to discuss a different quote.</Notice>}

      {b.status === "sent" && (
        <fieldset className="mt-6 space-y-4 border border-border bg-card p-5">
          <legend className="bg-card px-2 font-semibold">Your response</legend>
          <label className="flex items-start gap-3 text-sm"><input type="radio" name="booking-response" value="paid" checked={response === "paid"} disabled={busy} onChange={() => setResponse("paid")} className="mt-1" /> I've paid the {formatGBP(deposit)} deposit using the reference above.</label>
          <label className="flex items-start gap-3 text-sm"><input type="radio" name="booking-response" value="rejected" checked={response === "rejected"} disabled={busy} onChange={() => setResponse("rejected")} className="mt-1" /> I reject this job.</label>
          <Button disabled={!response || busy} variant={response === "rejected" ? "destructive" : "default"} onClick={accept}>{busy ? "Saving…" : response === "rejected" ? "Reject job" : "Confirm deposit paid"}</Button>
          {err && <p className="text-sm text-destructive">{err}</p>}
        </fieldset>
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return <section className="py-8 md:py-12"><div className="container-x"><div className="mx-auto max-w-4xl">{children}</div></div></section>;
}
function Notice({ children }: { children: React.ReactNode }) {
  return <div className="mt-4 flex items-center gap-2 rounded-lg bg-muted p-4 text-sm font-semibold">{children}</div>;
}
